/**
 * Authoritative marker-strip helper for the agent-transparency
 * uplift (Crucible #602 PR #C1, design §3a "Marker transport + strip
 * authority").
 *
 * Used by the chat reducer's text-chunk handler in `state.ts`. The
 * helper runs in the renderer and is the SINGLE site that decides
 * whether marker text reaches `ChatMessage.content`. Two stripping
 * sites would diverge (e.g. one upgraded to a new grammar before
 * the other), so PR #C2's `ChatBubble` defensive overlay runs the
 * same complete-marker regex but is a "last line defence" only —
 * the reducer is authoritative.
 *
 * The marker grammar (design §3a):
 *
 *   marker := "<!--" SP "crucible:activity:b64" SP payload SP "-->"
 *   payload := base64-encoded UTF-8 JSON
 *
 * Two stripping cases:
 *
 *   1. A complete marker on a single chunk: strip the whole comment.
 *   2. A marker split across two (or more) chunks: hold back the
 *      partial fragment until the rest arrives, then strip the
 *      reconstituted whole.
 *
 * The split case is the bit that makes a single regex-replace
 * insufficient. Design §3a v3.2 worked through the two cases as
 * tests #4 and #5; the implementation below mirrors that worked
 * example.
 *
 * Per-message buffer (see `MarkerStripBuffer`): the reducer keeps
 * one `pending` string per in-flight assistant `message.id`.
 * Concurrent messages cannot cross-contaminate. On message finalise
 * (terminal chunk for the message), any remaining `pending` is
 * flushed AS-IS to `message.content` — by then no further chunks
 * can complete the marker, so a hanging fragment is treated as
 * accidental literal text rather than silently suppressed.
 */

/** Greedy global regex matching a complete marker on a single
 *  input string. Non-greedy across `-->` so we never swallow
 *  intermediate prose between two adjacent markers. */
const CRUCIBLE_ACTIVITY_MARKER =
  /<!--\s*crucible:activity:b64\s+[A-Za-z0-9+/=]+\s*-->/g;

/**
 * Canonical-prefix shape of the marker. Used by the partial-match
 * helper to decide whether a trailing `<!--…` fragment could
 * eventually become a marker, or whether it's some other HTML
 * comment that should pass through.
 *
 * The runtime grammar accepts `\s+` between tokens, but for partial
 * detection we use a single canonical form (single space) — any
 * suffix that doesn't match this prefix isn't going to become a
 * marker no matter what arrives next.
 */
const MARKER_LITERAL_PREFIX = '<!-- crucible:activity:b64 ';

/** Stripped output + carry-over for the next chunk. */
export interface MarkerStripResult {
  stripped: string;
  pendingNext: string;
}

/**
 * Strip activity markers from a chunk, accounting for a held-back
 * partial marker from the previous chunk. Pure + deterministic so
 * the pinned-table tests can pin every case.
 *
 * Algorithm:
 *
 *   1. Join `pending` + `incoming`. This handles the cross-chunk
 *      case: if `pending` ends mid-marker and `incoming` carries
 *      the rest, the joined string contains the complete marker.
 *   2. Strip every complete marker via `CRUCIBLE_ACTIVITY_MARKER`.
 *   3. Find the LAST `<!--` in the remainder. If the suffix from
 *      there could still become a marker, hold it back as
 *      `pendingNext`. Otherwise (the `<!--` is a different HTML
 *      comment, or a marker shell that's already structurally
 *      invalid — e.g. payload contains a disallowed char), flush
 *      everything.
 *
 * Two hold-back conditions:
 *
 *   a) The suffix is a proper prefix of `MARKER_LITERAL_PREFIX`.
 *      The chunk was cut mid-literal — e.g. `<!-- crucible:activ`.
 *   b) The suffix starts with `MARKER_LITERAL_PREFIX` and contains
 *      no `-->`. The literal prefix arrived but the closing `-->`
 *      hasn't yet — the chunk was cut mid-payload.
 *
 * Anything else (e.g. `<!-- some other comment`, or a marker whose
 * payload has bytes outside the base64 charset already) flushes —
 * those won't become valid markers regardless of what arrives next.
 */
export function stripActivityMarkers(
  pending: string,
  incoming: string,
): MarkerStripResult {
  const joined = pending + incoming;
  const afterComplete = joined.replace(CRUCIBLE_ACTIVITY_MARKER, '');
  const lastOpen = afterComplete.lastIndexOf('<!--');
  if (lastOpen === -1) {
    return { stripped: afterComplete, pendingNext: '' };
  }
  const suffix = afterComplete.slice(lastOpen);
  const isLiteralPrefix = MARKER_LITERAL_PREFIX.startsWith(suffix);
  const isOpenMarker =
    suffix.startsWith(MARKER_LITERAL_PREFIX) && !suffix.includes('-->');
  if (isLiteralPrefix || isOpenMarker) {
    return {
      stripped: afterComplete.slice(0, lastOpen),
      pendingNext: suffix,
    };
  }
  return { stripped: afterComplete, pendingNext: '' };
}

/**
 * Per-message pending-buffer cache. Keyed by the in-flight
 * assistant `ChatMessage.id` so concurrent messages don't share
 * state. The reducer owns one of these on the state object; tests
 * inject a fresh instance per case.
 *
 * Cleared via `flush(messageId)` on the message's terminal chunk
 * (or on `game/changed` / `chat/loaded` which wipe the
 * conversation state entirely). The cache is in-memory only —
 * pending fragments don't survive a reducer re-init, by design.
 */
export class MarkerStripBuffer {
  private readonly pending = new Map<string, string>();

  /**
   * Apply a text chunk for a specific message. Returns the
   * cleaned text the reducer should append to `message.content`.
   * Caches the partial-marker carry-over for the next call on
   * the same message.
   */
  apply(messageId: string, incoming: string): string {
    const prev = this.pending.get(messageId) ?? '';
    const { stripped, pendingNext } = stripActivityMarkers(prev, incoming);
    if (pendingNext) {
      this.pending.set(messageId, pendingNext);
    } else {
      this.pending.delete(messageId);
    }
    return stripped;
  }

  /**
   * Flush any held-back partial marker for this message AS-IS.
   * Called on the message's terminal chunk. The flushed bytes
   * become literal text — see the design's "On finalise, pending is
   * flushed AS-IS" rule. Returns the bytes to append to
   * `message.content`, or `''` when nothing was pending.
   */
  flush(messageId: string): string {
    const pending = this.pending.get(messageId);
    if (!pending) return '';
    this.pending.delete(messageId);
    return pending;
  }

  /** Drop any held-back fragments for a message. Used on
   *  `chat/loaded` and `game/changed` where the whole conversation
   *  state is wiped. */
  clear(messageId: string): void {
    this.pending.delete(messageId);
  }

  /** Drop every held-back fragment. Idempotent. */
  clearAll(): void {
    this.pending.clear();
  }

  /** Test-only — peek at the pending fragments. */
  _testOnlyPending(): Map<string, string> {
    return new Map(this.pending);
  }
}
